import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { changerLangueTemporaire, LANGUES } from '../i18n';
import AutoInspectionParEtapes from './AutoInspectionParEtapes';

/**
 * AutoDeclarationPublique — page PUBLIQUE (sans compte) d'auto-déclaration d'inspection
 * résidentielle par le citoyen (accès par QR : /declaration/:tenantSlug).
 *
 * Consomme les endpoints publics du Lot 1 (aucun en-tête Authorization) :
 *   GET  /api/{slug}/public/auto-declaration/config       → { service_nom, grille }
 *   GET  /api/{slug}/public/auto-declaration/adresses?q=  → { items:[{id,adresse_civique,ville}] }
 *   POST /api/{slug}/public/auto-declaration              → { ok:true } (accusé neutre)
 *
 * Aucune donnée sensible n'est affichée. Rendu mobile-first, hors AuthProvider.
 *
 * Bilingue (FR | EN) : langue de l'appareil par défaut, ?lang=en|fr dans le lien,
 * bouton de bascule en haut. Les textes du service viennent de `traductions_en`
 * (traduction automatique, corrigeable par le service). La VALEUR enregistrée
 * reste toujours la chaîne française : la conformité et les déclencheurs
 * d'anomalie la comparent (services/auto_declaration.py).
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

// Types de champ NON gérés au Lot 2 (on ne rend que les questions à choix).
// Types structurels/médias non pertinents pour l'auto-déclaration citoyenne.
// Les types de saisie (texte/date/nombre) et de choix sont RENDUS fidèlement (cf. renderQuestion).
const TYPES_IGNORES = new Set([
  'photo', 'photos', 'signature', 'condition', 'info', 'titre', 'section',
]);

const COULEURS = {
  fond: '#f8fafc', carte: '#ffffff', bord: '#e2e8f0', texte: '#0f172a',
  sousTexte: '#64748b', primaire: '#dc2626', vert: '#16a34a', orange: '#f59e0b',
  accent: '#2563eb',
};

// Options par défaut quand la question n'en porte pas (miroir de InspectionTerrain).
const OPTIONS_DEFAUT = {
  conforme_non_conforme: ['Conforme', 'Non conforme', 'N/A'],
  oui_non: ['Oui', 'Non'],
  etat: ['Bon', 'Moyen', 'Mauvais'],
};

function questionsDeSection(section) {
  const arr = section.items || section.questions || section.champs || section.criteres || [];
  return Array.isArray(arr) ? arr : [];
}

function libelleQuestion(q) {
  return q.question || q.label || q.texte || q.nom || q.titre || '';
}

function cleQuestion(q, sIdx, qIdx) {
  return q.id || q.champ_id || q.key || q.field_key || `q_${sIdx}_${qIdx}`;
}

const AutoDeclarationPublique = () => {
  const { tenantSlug } = useParams();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const enAnglais = i18n.language === 'en';
  useEffect(() => {
    const lang = (searchParams.get('lang') || '').toLowerCase();
    if (LANGUES.includes(lang)) changerLangueTemporaire(lang);
  }, [searchParams]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);   // { service_nom, grille }
  const [indispo, setIndispo] = useState(false); // formulaire non disponible
  const [envoi, setEnvoi] = useState(false);
  const [envoye, setEnvoye] = useState(false);
  const [erreur, setErreur] = useState('');

  const [form, setForm] = useState({
    occupant_nom: '', proprietaire_nom: '', adresse: '', ville: '',
    code_postal: '', batiment_id_choisi: null, reponses_grille: {}, ne_pas_remplir: '',
  });

  // Autocomplétion d'adresse (adresse-only, debouncée)
  const [suggestions, setSuggestions] = useState([]);
  const debounceRef = useRef(null);

  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const r = await fetch(`${BACKEND}/api/${tenantSlug}/public/auto-declaration/config`);
        if (!r.ok) throw new Error('indisponible');
        const data = await r.json();
        if (!data || !data.grille) throw new Error('config vide');
        if (!annule) setConfig(data);
      } catch {
        if (!annule) setIndispo(true);
      } finally {
        if (!annule) setLoading(false);
      }
    })();
    return () => { annule = true; };
  }, [tenantSlug]);

  // Nettoie le timer de debounce au démontage (évite un setState sur composant démonté).
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const majAdresse = (valeur) => {
    // Toute frappe manuelle repasse en texte libre (on oublie le bâtiment choisi).
    setForm((prev) => ({ ...prev, adresse: valeur, batiment_id_choisi: null }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if ((valeur || '').trim().length < 3) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await fetch(
          `${BACKEND}/api/${tenantSlug}/public/auto-declaration/adresses?q=${encodeURIComponent(valeur.trim())}`);
        if (!r.ok) { setSuggestions([]); return; }
        const data = await r.json();
        setSuggestions(data.items || []);
      } catch { setSuggestions([]); }
    }, 300);
  };

  const choisirAdresse = (item) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);  // évite un menu qui réapparaît
    setForm((prev) => ({
      ...prev, adresse: item.adresse_civique || '', ville: item.ville || '',
      batiment_id_choisi: item.id,
    }));
    setSuggestions([]);
  };

  const repondre = (cle, valeur) => {
    setForm((prev) => ({ ...prev, reponses_grille: { ...prev.reponses_grille, [cle]: valeur } }));
  };

  const peutEnvoyer = form.occupant_nom.trim() && form.proprietaire_nom.trim()
    && form.adresse.trim() && form.ville.trim() && !envoi;

  const envoyer = async () => {
    setErreur(''); setEnvoi(true);
    try {
      const r = await fetch(`${BACKEND}/api/${tenantSlug}/public/auto-declaration`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          occupant_nom: form.occupant_nom, proprietaire_nom: form.proprietaire_nom,
          adresse: form.adresse, ville: form.ville, code_postal: form.code_postal,
          batiment_id_choisi: form.batiment_id_choisi, reponses_grille: form.reponses_grille,
          ne_pas_remplir: form.ne_pas_remplir,
        }),
      });
      if (r.status === 429) { setErreur(t('autoDeclaration.erreurTropDeTentatives')); return; }
      if (!r.ok) { setErreur(t('autoDeclaration.erreurGenerique')); return; }
      setEnvoye(true); // accusé NEUTRE (ne révèle pas le résultat du rapprochement)
    } catch {
      setErreur(t('autoDeclaration.erreurConnexion'));
    } finally { setEnvoi(false); }
  };

  // Texte saisi par le service → version anglaise si disponible, sinon français.
  const traduire = (texte) => {
    if (!enAnglais || !texte) return texte;
    const cle = String(texte).trim();
    const dict = (config && config.traductions_en) || {};
    if (dict[cle]) return dict[cle];
    // Choix par défaut (Conforme / Oui / Bon…) : traduits par l'application.
    const defauts = t('autoDeclaration.optionsDefaut', { returnObjects: true }) || {};
    return defauts[cle] || texte;
  };

  // ---- Rendu ----
  const bascule = (
    <div role="group" aria-label={t('autoDeclaration.langue')}
      style={{ display: 'flex', justifyContent: 'flex-end', gap: 4, marginBottom: '0.5rem' }}>
      {LANGUES.map((l) => {
        const actif = i18n.language === l;
        return (
          <button type="button" key={l} onClick={() => changerLangueTemporaire(l)} aria-pressed={actif}
            lang={l}
            style={{ padding: '0.3rem 0.7rem', fontSize: '0.8rem', fontWeight: 700, borderRadius: 6,
              cursor: 'pointer', border: `1px solid ${actif ? COULEURS.primaire : COULEURS.bord}`,
              background: actif ? COULEURS.primaire : COULEURS.carte, color: actif ? '#fff' : COULEURS.texte }}>
            {l.toUpperCase()}
          </button>
        );
      })}
    </div>
  );
  const page = (contenu) => (
    <div style={{ minHeight: '100vh', background: COULEURS.fond, padding: '1rem',
      fontFamily: 'system-ui, -apple-system, sans-serif', color: COULEURS.texte }}>
      <div style={{ maxWidth: 560, margin: '0 auto' }}>{bascule}{contenu}</div>
    </div>
  );

  if (loading) return page(<p style={{ textAlign: 'center', color: COULEURS.sousTexte }}>{t('autoDeclaration.chargement')}</p>);
  if (indispo || !config) return page(
    <div style={{ background: COULEURS.carte, border: `1px solid ${COULEURS.bord}`,
      borderRadius: 12, padding: '1.5rem', textAlign: 'center' }}>
      <h2 style={{ margin: 0 }}>{t('autoDeclaration.indisponibleTitre')}</h2>
      <p style={{ color: COULEURS.sousTexte }}>{t('autoDeclaration.indisponibleTexte')}</p>
    </div>);
  // Réglage Prévention « par étapes » (opt-in) : formulaire multi-étapes dédié.
  if (config.par_etapes) return page(
    <AutoInspectionParEtapes config={config} tenantSlug={tenantSlug} backend={BACKEND} />);
  if (envoye) return page(
    <div style={{ background: COULEURS.carte, border: `1px solid ${COULEURS.bord}`,
      borderRadius: 12, padding: '2rem', textAlign: 'center' }}>
      <div style={{ fontSize: '2.5rem' }}>✅</div>
      <h2>{t('autoDeclaration.merciTitre')}</h2>
      <p style={{ color: COULEURS.sousTexte }}>{t('autoDeclaration.merciTexte')}</p>
    </div>);

  const grille = config.grille || {};
  const sections = Array.isArray(grille.sections) ? grille.sections : [];

  const champ = (label, props) => (
    <label style={{ display: 'block', marginBottom: '0.75rem' }}>
      <span style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>{label}</span>
      <input {...props} style={{ width: '100%', padding: '0.6rem 0.75rem', fontSize: '1rem',
        border: `1px solid ${COULEURS.bord}`, borderRadius: 8, boxSizing: 'border-box' }} />
    </label>
  );

  const boutonChoix = (cle, valeur, texte, couleur) => {
    const actif = form.reponses_grille[cle] === valeur;
    return (
      <button type="button" key={valeur} onClick={() => repondre(cle, valeur)} aria-pressed={actif}
        style={{ flex: '1 1 auto', minWidth: 88, padding: '0.5rem', fontSize: '0.85rem', borderRadius: 8, cursor: 'pointer',
          border: `1px solid ${actif ? couleur : COULEURS.bord}`,
          background: actif ? couleur : COULEURS.carte, color: actif ? '#fff' : COULEURS.texte,
          fontWeight: actif ? 700 : 500 }}>
        {texte}
      </button>);
  };

  const couleurOption = (opt) => {
    const s = (opt || '').trim().toLowerCase();
    if (s.includes('non conforme') || s.includes('non-conforme')) return COULEURS.primaire;
    if (s.includes('conforme')) return COULEURS.vert;
    if (['s.o.', 'so', 's/o', 'n/a', 'na'].includes(s)) return COULEURS.sousTexte;
    return COULEURS.accent;
  };

  const styleSaisie = {
    width: '100%', padding: '0.55rem 0.7rem', fontSize: '1rem',
    border: `1px solid ${COULEURS.bord}`, borderRadius: 8, boxSizing: 'border-box',
  };

  // Rend une question FIDÈLEMENT selon son type + ses options (valeur stockée = la
  // chaîne d'option, comme une inspection normale → les déclencheurs d'anomalie matchent).
  const renderQuestion = (q, cle) => {
    const type = (q.type || '').toLowerCase();
    const opts = (Array.isArray(q.options) && q.options.length) ? q.options : (OPTIONS_DEFAUT[type] || []);
    const val = form.reponses_grille[cle];

    if (type === 'checkbox') {
      const sel = Array.isArray(val) ? val : [];
      return (
        <div role="group" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {opts.map((opt, i) => {
            const actif = sel.includes(opt);
            return (
              <button type="button" key={i} aria-pressed={actif}
                onClick={() => repondre(cle, actif ? sel.filter((v) => v !== opt) : [...sel, opt])}
                style={{ flex: '1 1 auto', minWidth: 88, padding: '0.5rem', fontSize: '0.85rem',
                  borderRadius: 8, cursor: 'pointer',
                  border: `1px solid ${actif ? couleurOption(opt) : COULEURS.bord}`,
                  background: actif ? couleurOption(opt) : COULEURS.carte,
                  color: actif ? '#fff' : COULEURS.texte, fontWeight: actif ? 700 : 500 }}>
                {traduire(opt)}
              </button>
            );
          })}
        </div>
      );
    }
    if (type === 'date') {
      return <input type="date" value={val || ''} onChange={(e) => repondre(cle, e.target.value)} style={styleSaisie} />;
    }
    if (type === 'number' || type === 'nombre') {
      return <input type="number" value={val ?? ''} onChange={(e) => repondre(cle, e.target.value)} style={styleSaisie} />;
    }
    if (type === 'texte' || type === 'text' || type === 'textarea') {
      return <textarea value={val || ''} rows={2} onChange={(e) => repondre(cle, e.target.value)}
        style={{ ...styleSaisie, resize: 'vertical' }} />;
    }
    // Choix (radio / conforme_non_conforme / oui_non / etat / select / liste) ou tout type doté d'options.
    if (opts.length) {
      return (
        <div role="group" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {opts.map((opt) => boutonChoix(cle, opt, traduire(opt), couleurOption(opt)))}
        </div>
      );
    }
    // Repli : type inconnu sans options → saisie libre.
    return <input value={val || ''} onChange={(e) => repondre(cle, e.target.value)} style={styleSaisie} />;
  };

  return page(
    <div>
      <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
        {config.logo_url && (
          <img src={config.logo_url} alt={config.service_nom || t('autoDeclaration.logo')}
            style={{ maxHeight: 84, maxWidth: '70%', objectFit: 'contain', marginBottom: '0.75rem' }} />
        )}
        <h1 style={{ margin: '0 0 0.25rem' }}>{t('autoDeclaration.titre')}</h1>
        <p style={{ color: COULEURS.sousTexte, margin: 0 }}>{config.service_nom}</p>
      </div>

      <div style={{ background: COULEURS.carte, border: `1px solid ${COULEURS.bord}`,
        borderRadius: 12, padding: '1.25rem', marginBottom: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>{t('autoDeclaration.votreAdresse')}</h3>
        <div style={{ position: 'relative' }}>
          {champ(t('autoDeclaration.adresseCivique'), {
            value: form.adresse, onChange: (e) => majAdresse(e.target.value),
            placeholder: t('autoDeclaration.adresseExemple'), autoComplete: 'off',
          })}
          {suggestions.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 5,
              background: COULEURS.carte, border: `1px solid ${COULEURS.bord}`, borderRadius: 8,
              marginTop: -6, maxHeight: 220, overflowY: 'auto', boxShadow: '0 8px 20px rgba(0,0,0,.08)' }}>
              {suggestions.map((it) => (
                <button type="button" key={it.id} onClick={() => choisirAdresse(it)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none',
                    background: COULEURS.carte, padding: '0.6rem 0.75rem', cursor: 'pointer',
                    fontSize: '0.95rem', borderBottom: `1px solid ${COULEURS.fond}` }}>
                  {it.adresse_civique} <span style={{ color: COULEURS.sousTexte }}>— {it.ville}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {champ(t('autoDeclaration.ville'), { value: form.ville, onChange: (e) => setForm((p) => ({ ...p, ville: e.target.value })) })}
        {champ(t('autoDeclaration.codePostal'), { value: form.code_postal, onChange: (e) => setForm((p) => ({ ...p, code_postal: e.target.value })) })}
      </div>

      <div style={{ background: COULEURS.carte, border: `1px solid ${COULEURS.bord}`,
        borderRadius: 12, padding: '1.25rem', marginBottom: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>{t('autoDeclaration.identification')}</h3>
        {champ(t('autoDeclaration.nomOccupant'), { value: form.occupant_nom, onChange: (e) => setForm((p) => ({ ...p, occupant_nom: e.target.value })) })}
        {champ(t('autoDeclaration.nomProprietaire'), { value: form.proprietaire_nom, onChange: (e) => setForm((p) => ({ ...p, proprietaire_nom: e.target.value })) })}
        {/* Honeypot anti-bot : nom NEUTRE (surtout pas « website »/« url »… que
            l'autofill des navigateurs et les gestionnaires de mots de passe
            remplissent) + masqué en display:none (que l'autofill ignore) — sinon
            un citoyen légitime dont le navigateur pré-remplit ce champ est rejeté. */}
        <input type="text" name="ne_pas_remplir" tabIndex={-1} autoComplete="off" aria-hidden="true"
          value={form.ne_pas_remplir} onChange={(e) => setForm((p) => ({ ...p, ne_pas_remplir: e.target.value }))}
          style={{ display: 'none' }} />
      </div>

      <div style={{ background: COULEURS.carte, border: `1px solid ${COULEURS.bord}`,
        borderRadius: 12, padding: '1.25rem', marginBottom: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>{traduire(grille.nom) || t('autoDeclaration.grilleParDefaut')}</h3>
        {sections.map((section, sIdx) => (
          <div key={sIdx} style={{ marginBottom: '1rem' }}>
            {section.titre && <div style={{ fontWeight: 700, margin: '0.5rem 0' }}>{traduire(section.titre)}</div>}
            {questionsDeSection(section).map((q, qIdx) => {
              const type = (q.type || '').toLowerCase();
              const label = libelleQuestion(q);
              if (!label || TYPES_IGNORES.has(type)) return null;
              const cle = cleQuestion(q, sIdx, qIdx);
              const labelId = `q-${cle}`;
              return (
                <div key={cle} style={{ marginBottom: '0.75rem' }}>
                  <div id={labelId} style={{ fontSize: '0.9rem', marginBottom: 6 }}>{traduire(label)}</div>
                  <div aria-labelledby={labelId}>{renderQuestion(q, cle)}</div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {erreur && <p style={{ color: COULEURS.primaire, textAlign: 'center' }}>{erreur}</p>}
      <button type="button" onClick={envoyer} disabled={!peutEnvoyer}
        style={{ width: '100%', padding: '0.9rem', fontSize: '1.05rem', fontWeight: 700,
          borderRadius: 10, border: 'none', color: '#fff', cursor: peutEnvoyer ? 'pointer' : 'not-allowed',
          background: peutEnvoyer ? COULEURS.vert : '#94a3b8' }}>
        {envoi ? t('autoDeclaration.envoi') : t('autoDeclaration.transmettre')}
      </button>
      <p style={{ fontSize: '0.75rem', color: COULEURS.sousTexte, textAlign: 'center', marginTop: '0.75rem' }}>
        {t('autoDeclaration.mentionVerification')}
      </p>
    </div>
  );
};

export default AutoDeclarationPublique;
